import React from 'react';
import { useTranslation } from 'react-i18next';
import { css, keyframes } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../styles';

interface AnnualPricingCardProps {
  annualPrice: number;
  currencySymbol: string;
  weeklyEquivalent: number;
  originalPrice?: number;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const AnnualPricingCard: React.FC<AnnualPricingCardProps> = ({
  annualPrice,
  currencySymbol,
  weeklyEquivalent,
  originalPrice,
}) => {
  const { t } = useTranslation();
  return (
    <div
      css={css`
        border: 0.5px solid ${colors.cloud};
        border-radius: 8px;
        padding: 24px;
        text-align: center;
        background: ${colors.fog};
        animation: ${fadeIn} 0.4s ease-out forwards;

        html[data-theme='dark'] & {
          background: ${colors.tungsten};
        }
      `}
    >
      <div
        css={css`
          ${fontStyles.cat};
          color: ${colors.silver};
          margin-bottom: ${spacing.centi};
          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        {t('subscription.yearlyPlan')}
      </div>

      <div
        css={css`
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: ${spacing.centi};
        `}
      >
        <span
          css={css`
            ${fontStyles.shark};
            color: ${colors.liquorice};

            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }
          `}
        >
          {currencySymbol}
          {annualPrice} {t('subscription.perYearAbbr')}
        </span>
        {originalPrice && originalPrice !== annualPrice && (
          <span
            css={css`
              ${fontStyles.cat};
              color: ${colors.silver};
              text-decoration: line-through;
            `}
          >
            {currencySymbol}
            {originalPrice}
          </span>
        )}
      </div>

      <div
        css={css`
          margin-top: ${spacing.centi};
        `}
      >
        <span
          css={css`
            ${fontStyles.flea};
            background: ${colors.petrolBlue};
            color: ${colors.sugar};
            border-radius: 16px;
            padding: 4px 12px;
            display: inline-block;
          `}
        >
          {t('subscription.pricePerWeek', { price: `${currencySymbol}${weeklyEquivalent.toFixed(2)}` })}
        </span>
      </div>
    </div>
  );
};
